/**
 * "Hand-drawn" example — demonstrates the sketchy, Excalidraw-like
 * rendering theme enabled via `model.setAppearance`.
 *
 * Exercises everything the hand-drawn renderer must handle: all six node
 * shapes, a group node, fan-out edges (bend points from orthogonal
 * routing), labeled edges, and varied node background/border colors.
 * Used for visual regression testing.
 */
import {
  Blueprint,
  component,
  createModel,
  metrics,
  type StyleRuleDescriptor,
} from "@diagram/sim-model";

/** A blueprint that owns a metric, turning it into a group node. */
class GroupBlueprint extends Blueprint {
  static params = {
    counter: component.ref(metrics.Counter),
  };

  declare params: typeof GroupBlueprint.params;

  engineOnStart() {}
}

/** Routes to two targets — the fan-out forces edge bend points. */
class RouterBlueprint extends Blueprint {
  static params = {
    primary: component.ref(Blueprint),
    fallback: component.ref(Blueprint),
  };

  declare params: typeof RouterBlueprint.params;

  engineOnStart() {}
}

/** A blueprint that references one node; the param key labels the edge. */
class ConnectedBlueprint extends Blueprint {
  static params = {
    stores: component.ref(Blueprint),
  };

  declare params: typeof ConnectedBlueprint.params;

  engineOnStart() {}
}

/** Minimal blueprint with no params. */
class LeafBlueprint extends Blueprint {
  engineOnStart() {}
}

export const model = createModel();

// --- Metrics (owning a metric makes the parent a group) -----------------------
const groupCounter = model.create("groupCounter", metrics.Counter);

// --- Leaf nodes — cylinder, circle, hexagon -----------------------------------

const databaseNode = model.create(
  "database",
  LeafBlueprint,
  {},
  { label: "Database" },
);

const cacheNode = model.create("cache", LeafBlueprint, {}, { label: "Cache" });

const queueNode = model.create("queue", LeafBlueprint, {}, { label: "Queue" });

// --- Connected nodes — rectangle, rounded rectangle, diamond ------------------

const serviceA = model.create(
  "service-a",
  ConnectedBlueprint,
  { stores: databaseNode },
  { label: "Service A" },
);

const serviceB = model.create(
  "service-b",
  ConnectedBlueprint,
  { stores: cacheNode },
  { label: "Service B" },
);

// The router fans out to both services, producing labeled edges
// ("primary" / "fallback") with bend points from orthogonal routing.
model.create(
  "router",
  RouterBlueprint,
  { primary: serviceA, fallback: serviceB },
  { label: "Router" },
);

// --- Group node (owns a metric) -----------------------------------------------
model.create(
  "ingest-group",
  GroupBlueprint,
  { counter: groupCounter },
  {
    label: "Ingest",
    description: "A group node that owns a counter metric",
  },
);

// Suppress unused-variable warnings
void queueNode;

// ---------------------------------------------------------------------------
// Style rules — one shape per node, with varied backgrounds and borders so
// the sketchy fill and stroke colors are exercised.
// ---------------------------------------------------------------------------

const styles: StyleRuleDescriptor[] = [
  {
    name: "shape-diamond",
    match: { id: "router" },
    style: { shape: "diamond", background: "#fdf2d0", borderColor: "#b8860b" },
  },
  {
    name: "shape-rectangle",
    match: { id: "service-a" },
    style: {
      shape: "rectangle",
      background: "#dceefb",
      borderColor: "#1d4ed8",
    },
  },
  {
    name: "shape-rounded-rect",
    match: { id: "service-b" },
    style: {
      shape: "rounded-rectangle",
      background: "#e8f8ef",
      borderColor: "#2ecc71",
    },
  },
  {
    name: "shape-cylinder",
    match: { id: "database" },
    style: { shape: "cylinder", background: "#f3e8ff", borderColor: "#7c3aed" },
  },
  {
    name: "shape-circle",
    match: { id: "cache" },
    style: { shape: "circle", background: "#ffe8e0", borderColor: "#e74c3c" },
  },
  {
    name: "shape-hexagon",
    match: { id: "queue" },
    style: { shape: "hexagon", background: "#fff7cc", borderColor: "#f1c40f" },
  },
];

model.addStyleRules(styles);

// Default this diagram to the hand-drawn (sketchy) rendering theme.
model.setAppearance({ theme: "hand-drawn" });
